import React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/shared/PageHeader';
import LoadingLock from '@/components/shared/LoadingLock';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ArrowRight, Plus, Save, Trash2 } from 'lucide-react';
import SearchableSelect, { type SearchableOption } from '@/components/shared/SearchableSelect';
import RowAccountPickerField from '@/components/document-entry/RowAccountPickerField';
import DocumentRowInput from '@/components/document-entry/DocumentRowInput';
import { parseAccountCodeProject } from '@/lib/accountingAccountProjectCode';

type FocusedEntryCell = {
  rowId: string;
  field: 'description' | 'debit' | 'credit';
};
import { accountingApi, type AccountingHeadingListItem } from '@/lib/api/accounting';
import { loadFullHeadingCatalog, type HeadingFlat } from '@/document-entry/headingCatalog';
import { cn } from '@/lib/utils';
import PayrollDocumentImportPanel from '@/components/payroll/PayrollDocumentImportPanel';
import {
  buildPayrollAccrualDocumentRows,
  payrollAccrualDescription,
  type PayrollDocumentEntryRow,
} from '@/lib/payroll/buildPayrollAccrualDocument';
import type { PayrollDraft } from '@/lib/payroll/payrollDraftTypes';

const PROJECT_STATUS_SORT_RANK: Record<string, number> = {
  in_progress: 0,
  planning: 1,
  paused: 2,
  completed: 3,
  cancelled: 4,
};

function sortProjectHeadingItems(items: AccountingHeadingListItem[]): AccountingHeadingListItem[] {
  return [...items].sort((a, b) => {
    const ra = PROJECT_STATUS_SORT_RANK[a.status ?? ''] ?? 50;
    const rb = PROJECT_STATUS_SORT_RANK[b.status ?? ''] ?? 50;
    if (ra !== rb) return ra - rb;
    return (a.title || '').localeCompare(b.title || '', 'fa');
  });
}

function projectItemsToOptions(items: AccountingHeadingListItem[]): SearchableOption[] {
  return sortProjectHeadingItems(items).map((p) => ({
    value: String(p.entity_id ?? p.id),
    label: p.title,
    searchText: `${p.title} ${p.code} ${p.status_label || ''}`,
  }));
}
import { readPayrollDocSettings } from '@/lib/payroll/payrollDocSettings';
import { readPayrollDraft } from '@/lib/payrollCalculator';

type EntryRow = {
  id: string;
  accountCode: string;
  expenseTypeId: string;
  projectId: string;
  /** شناسه ترکیبی مثل employee-123 از lookups.beneficiaries */
  beneficiaryKey: string;
  sourceBankAccountId: string;
  destinationBankAccountId: string;
  paymentMethod: string;
  referenceNo: string;
  description: string;
  debit: string;
  credit: string;
};

function emptyDocumentRow(seed: string): EntryRow {
  return {
    id: seed,
    accountCode: '',
    expenseTypeId: '',
    projectId: '',
    beneficiaryKey: '',
    sourceBankAccountId: '',
    destinationBankAccountId: '',
    paymentMethod: '',
    referenceNo: '',
    description: '',
    debit: '',
    credit: '',
  };
}

function todayFaDate(): string {
  const formatted = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  return formatted.replace(/[^\d/۰-۹]/g, '');
}

function nextDocNumber(): string {
  return '1';
}

function toNumber(value: string): number {
  const n = Number((value || '').replace(/,/g, '').replace(/\D/g, ''));
  return Number.isFinite(n) ? n : 0;
}

function formatThousands(value: string): string {
  const n = toNumber(value);
  if (!n) return '';
  return n.toLocaleString('en-US');
}

const AccountingDocumentEntryPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const presetProjectId = searchParams.get('projectId')?.trim() || '';
  const fromPayroll = searchParams.get('fromPayroll') === '1';
  const isEditMode = Boolean(id);
  const payrollDraft = React.useMemo(() => {
    if (!fromPayroll || isEditMode) return null;
    return readPayrollDraft<PayrollDraft>();
  }, [fromPayroll, isEditMode]);
  const [docNo, setDocNo] = React.useState(() => nextDocNumber());
  const [docDate, setDocDate] = React.useState(() => todayFaDate());
  const [operationDescription, setOperationDescription] = React.useState('');
  const [headingCatalog, setHeadingCatalog] = React.useState<HeadingFlat[]>([]);
  const [loadingHeadingCatalog, setLoadingHeadingCatalog] = React.useState(true);
  const [loadingProjects, setLoadingProjects] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [loadingDoc, setLoadingDoc] = React.useState(false);
  const [projectOptions, setProjectOptions] = React.useState<SearchableOption[]>([
    { value: '', label: 'بدون پروژه' },
  ]);
  const seed = `${Date.now()}`;
  const [rows, setRows] = React.useState<EntryRow[]>(() => {
    const first = emptyDocumentRow(`r-${seed}-a`);
    const second = emptyDocumentRow(`r-${seed}-b`);
    if (presetProjectId) {
      first.projectId = presetProjectId;
      second.projectId = presetProjectId;
    }
    return [first, second];
  });
  const [expenseTypeOptions, setExpenseTypeOptions] = React.useState<SearchableOption[]>([]);
  const [focusedCell, setFocusedCell] = React.useState<FocusedEntryCell | null>(null);
  const [operationDescFocused, setOperationDescFocused] = React.useState(false);
  const payrollAutoApplied = React.useRef(false);

  React.useEffect(() => {
    if (!payrollDraft || payrollAutoApplied.current || loadingDoc) return;
    const built = buildPayrollAccrualDocumentRows(payrollDraft, readPayrollDocSettings());
    const hasAmounts = built.some((r) => r.debit || r.credit);
    if (!hasAmounts) return;
    payrollAutoApplied.current = true;
    setRows(built as EntryRow[]);
    setOperationDescription(payrollAccrualDescription(payrollDraft));
  }, [payrollDraft, loadingDoc]);

  React.useEffect(() => {
    let mounted = true;

    void loadFullHeadingCatalog()
      .then((catalog) => {
        if (mounted) setHeadingCatalog(catalog);
      })
      .catch(() => {
        if (mounted) setHeadingCatalog([]);
      })
      .finally(() => {
        if (mounted) setLoadingHeadingCatalog(false);
      });

    void Promise.allSettled([
      accountingApi.accountingHeadingsList({ level: 'project' }),
      accountingApi.lookups(),
    ])
      .then(([projectsResult, lookupsResult]) => {
        if (!mounted) return;

        if (lookupsResult.status === 'fulfilled') {
          setExpenseTypeOptions(
            (lookupsResult.value.expense_types || [])
              .filter((x) => x.is_active)
              .map((x) => ({ value: String(x.id), label: x.title, searchText: `${x.title} ${x.note || ''}` })),
          );
        } else {
          setExpenseTypeOptions([]);
        }

        if (projectsResult.status === 'fulfilled' && projectsResult.value.length > 0) {
          setProjectOptions([{ value: '', label: 'بدون پروژه' }, ...projectItemsToOptions(projectsResult.value)]);
          return;
        }

        if (lookupsResult.status === 'fulfilled') {
          setProjectOptions([
            { value: '', label: 'بدون پروژه' },
            ...lookupsResult.value.projects.map((p) => ({ value: String(p.id), label: p.label })),
          ]);
          return;
        }

        setProjectOptions([{ value: '', label: 'بدون پروژه' }]);
      })
      .finally(() => {
        if (mounted) setLoadingProjects(false);
      });

    if (!isEditMode) {
      void accountingApi
        .nextAccountingDocumentNumber()
        .then((nextNo) => {
          if (mounted) setDocNo(nextNo);
        })
        .catch(() => {
          /* keep default doc number */
        });
    }

    return () => {
      mounted = false;
    };
  }, [isEditMode]);

  React.useEffect(() => {
    if (!id) return;
    setLoadingDoc(true);
    accountingApi
      .accountingDocument(id)
      .then((doc) => {
        setDocNo(doc.docNo || '');
        setDocDate(doc.docDate || todayFaDate());
        setOperationDescription(doc.operationDescription || '');
        const mappedRows: EntryRow[] = (doc.rows || []).map((r, idx) => ({
          id: r.id || `r-load-${Date.now()}-${idx + 1}`,
          accountCode: r.accountCode || '',
          expenseTypeId: r.expenseTypeId ? String(r.expenseTypeId) : '',
          projectId: r.projectId ? String(r.projectId) : '',
          beneficiaryKey: '',
          sourceBankAccountId: r.sourceBankAccountId ? String(r.sourceBankAccountId) : '',
          destinationBankAccountId: r.destinationBankAccountId ? String(r.destinationBankAccountId) : '',
          paymentMethod: r.paymentMethod ? String(r.paymentMethod) : '',
          referenceNo: r.referenceNo ? String(r.referenceNo) : '',
          description: r.accountTitle || '',
          debit: r.debit ? Number(r.debit).toLocaleString('en-US') : '',
          credit: r.credit ? Number(r.credit).toLocaleString('en-US') : '',
        }));
        const t = `${Date.now()}`;
        const fallbackRows: EntryRow[] = [
          emptyDocumentRow(`r-${t}-pad-1`),
          emptyDocumentRow(`r-${t}-pad-2`),
        ];
        setRows(mappedRows.length >= 2 ? mappedRows : [...mappedRows, ...fallbackRows].slice(0, 2));
      })
      .catch(() => navigate('/accounting-documents'))
      .finally(() => setLoadingDoc(false));
  }, [id, navigate]);

  const totalDebit = React.useMemo(
    () => rows.reduce((sum, row) => sum + toNumber(row.debit), 0),
    [rows],
  );
  const totalCredit = React.useMemo(
    () => rows.reduce((sum, row) => sum + toNumber(row.credit), 0),
    [rows],
  );
  const difference = totalDebit - totalCredit;

  const colDescriptionClass =
    focusedCell?.field === 'description' ? 'min-w-[26rem] w-[26rem]' : 'min-w-[10rem] w-[10rem]';
  const colDebitClass =
    focusedCell?.field === 'debit' ? 'min-w-[12rem] w-[12rem]' : 'min-w-[6rem] w-[6rem]';
  const colCreditClass =
    focusedCell?.field === 'credit' ? 'min-w-[12rem] w-[12rem]' : 'min-w-[6rem] w-[6rem]';
  const updateRow = (rowId: string, key: keyof EntryRow, value: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? {
              ...row,
              [key]:
                key === 'debit' || key === 'credit'
                  ? formatThousands(value)
                  : value,
            }
          : row,
      ),
    );
  };

  const defaultProjectIdForNewRow = React.useCallback(
    () => presetProjectId || rows.find((r) => r.projectId)?.projectId || '',
    [presetProjectId, rows],
  );

  const addRow = () => {
    const next = emptyDocumentRow(`r-${Date.now()}-${rows.length + 1}`);
    next.projectId = defaultProjectIdForNewRow();
    setRows((prev) => [...prev, next]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length <= 2 ? prev : prev.filter((row) => row.id !== id)));
  };

  const applyPayrollDocumentRows = (payrollRows: PayrollDocumentEntryRow[], opDesc: string) => {
    payrollAutoApplied.current = true;
    setRows(payrollRows as EntryRow[]);
    setOperationDescription(opDesc);
  };

  const saveDocument = () => {
    const mapped = rows.map((r) => {
      const parsed = parseAccountCodeProject(r.accountCode);
      const baseAccountCode = parsed.hasProjectSuffix ? parsed.baseAccountCode : r.accountCode.trim();
      const projectIdFromField = r.projectId ? Number(r.projectId) : null;
      const projectIdFromCode = parsed.hasProjectSuffix ? parsed.projectId : null;
      const project_id =
        projectIdFromField != null && projectIdFromField > 0
          ? projectIdFromField
          : projectIdFromCode;
      return {
        account_code: baseAccountCode,
        account_title: r.description.trim(),
        expense_type_id: r.expenseTypeId ? Number(r.expenseTypeId) : null,
        project_id,
        counterparty_type: null,
        counterparty_id: null,
        source_bank_account_id: null,
        destination_bank_account_id: null,
        payment_method: null,
        reference_no: r.referenceNo.trim() || null,
        debit: toNumber(r.debit),
        credit: toNumber(r.credit),
      };
    });

    const sharedProjectId =
      mapped.find((r) => r.project_id != null && Number(r.project_id) > 0)?.project_id ?? null;

    const cleanRows = mapped
      .map((r) => {
        if (
          sharedProjectId &&
          (r.debit > 0 || r.credit > 0) &&
          (r.project_id == null || Number(r.project_id) <= 0)
        ) {
          return { ...r, project_id: sharedProjectId };
        }
        return r;
      })
      .filter((r) => {
        const hasExpenseType = r.expense_type_id != null && Number(r.expense_type_id) > 0;
        const hasRef = !!(r.reference_no && r.reference_no.length > 0);
        return (
          r.account_code ||
          r.account_title ||
          hasExpenseType ||
          hasRef ||
          r.debit > 0 ||
          r.credit > 0
        );
      });

    setSaving(true);
    const req = isEditMode && id
      ? accountingApi.updateAccountingDocument(id, {
          doc_date: docDate,
          operation_description: operationDescription.trim(),
          rows: cleanRows,
        })
      : accountingApi.createAccountingDocument({
          doc_date: docDate,
          operation_description: operationDescription.trim(),
          rows: cleanRows,
        });
    req
      .then((created) => {
        const linkedProjectId = cleanRows.find((r) => r.project_id != null && Number(r.project_id) > 0)?.project_id;
        if (linkedProjectId) {
          navigate(`/projects/${linkedProjectId}/overview`);
          return;
        }
        navigate(`/accounting-documents/${created.id}`);
      })
      .catch(() => {
        // noop: getJson/sendJson surfaces message via thrown errors elsewhere
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title={isEditMode ? `ویرایش سند حسابداری ${docNo ? `شماره ${docNo}` : ''}` : 'ثبت سند حسابداری'}
        description="ثبت/ویرایش سند با کنترل تراز بدهکار/بستانکار"
        actions={
          <Button asChild variant="outline" className="gap-2">
            <Link to="/accounting-documents">
              <ArrowRight className="h-4 w-4" />
              بازگشت
            </Link>
          </Button>
        }
      />

      <LoadingLock loading={loadingDoc}>
      {payrollDraft ? (
        <PayrollDocumentImportPanel
          draft={payrollDraft}
          headingCatalog={headingCatalog}
          onApplyRows={applyPayrollDocumentRows}
        />
      ) : fromPayroll && !isEditMode ? (
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="py-4 text-sm text-amber-950">
            پیش‌نویس حقوق یافت نشد. از{' '}
            <Link to="/payroll-calculator" className="font-medium text-indigo-700 underline">
              محاسبه حقوق
            </Link>{' '}
            دوباره «سند استقرار حقوق» را بزنید.
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">مشخصات سند</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs text-slate-600">شماره سند</label>
            <Input value={docNo} readOnly />
          </div>
          <div className="md:col-span-2">
            <label className="mb-1 block text-xs text-slate-600">تاریخ سند</label>
            <Input value={docDate} onChange={(e) => setDocDate(e.target.value)} />
          </div>
          <div className="md:col-span-8">
            <label className="mb-1 block text-xs text-slate-600">شرح عملیات</label>
            <Input
              value={operationDescription}
              onChange={(e) => setOperationDescription(e.target.value)}
              onFocus={() => setOperationDescFocused(true)}
              onBlur={() => setOperationDescFocused(false)}
              placeholder="شرح کلی عملیات سند"
              className={
                operationDescFocused
                  ? 'h-11 text-base font-medium ring-2 ring-sky-200'
                  : 'h-9 text-sm'
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">آیتم‌های سند</CardTitle>
          <p className="text-xs text-slate-500">
            کد حساب جدا از پروژه است — پروژه را در ستون «پروژه» انتخاب کنید. از صفحه پروژه وارد شوید تا
            همان پروژه پیش‌فرض شود.
            {presetProjectId ? (
              <span className="ms-1 font-medium text-sky-700">
                پروژهٔ جاری: #
                {presetProjectId}
              </span>
            ) : null}
          </p>
        </CardHeader>
        <CardContent>
          {loadingDoc ? <div className="mb-3 text-sm text-slate-500">در حال بارگذاری سند...</div> : null}
          <div className="overflow-x-auto rounded-md border">
            <table className="w-max min-w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-slate-700">
                  <th className="border px-2 py-2 text-right">کد حساب</th>
                  <th className="border px-2 py-2 text-right">پروژه</th>
                  <th className="border px-2 py-2 text-right">نوع هزینه</th>
                  <th className="border px-2 py-2 text-right">شناسه / پیگیری</th>
                  <th className={cn('border px-2 py-2 text-right transition-all', colDescriptionClass)}>شرح</th>
                  <th className={cn('border px-2 py-2 text-right transition-all', colDebitClass)}>بدهکار</th>
                  <th className={cn('border px-2 py-2 text-right transition-all', colCreditClass)}>بستانکار</th>
                  <th className="border px-2 py-2 text-right">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="border px-2 py-2">
                      <RowAccountPickerField
                        accountCode={row.accountCode}
                        onAccountCodeChange={(v) => updateRow(row.id, 'accountCode', v)}
                        headingCatalog={headingCatalog}
                        projectOptions={projectOptions}
                        projectId={row.projectId}
                        onProjectIdChange={(v) => updateRow(row.id, 'projectId', v)}
                        loadingHeadingCatalog={loadingHeadingCatalog}
                        loadingProjects={loadingProjects}
                        mode="accountOnly"
                        disabled={loadingDoc}
                      />
                    </td>
                    <td className="border px-2 py-2 min-w-[12rem]">
                      <SearchableSelect
                        value={row.projectId}
                        onChange={(value) => updateRow(row.id, 'projectId', value)}
                        options={projectOptions}
                        placeholder="انتخاب پروژه"
                        disabled={loadingDoc || loadingProjects}
                      />
                    </td>
                    <td className="border px-2 py-2">
                      <SearchableSelect
                        value={row.expenseTypeId}
                        onChange={(value) => updateRow(row.id, 'expenseTypeId', value)}
                        options={expenseTypeOptions}
                        placeholder="انتخاب نوع هزینه"
                      />
                    </td>
                    <td className="border px-2 py-2">
                      <Input
                        dir="ltr"
                        value={row.referenceNo}
                        onChange={(e) => updateRow(row.id, 'referenceNo', e.target.value)}
                        placeholder="پیگیری یا شماره فیش"
                        className="text-xs font-mono"
                      />
                    </td>
                    <td className={cn('border px-2 py-2 align-top transition-all', colDescriptionClass)}>
                      <DocumentRowInput
                        value={row.description}
                        onChange={(v) => updateRow(row.id, 'description', v)}
                        placeholder="شرح ردیف"
                        variant="text"
                        disabled={loadingDoc}
                        active={focusedCell?.rowId === row.id && focusedCell.field === 'description'}
                        onFocus={(e) => {
                          setFocusedCell({ rowId: row.id, field: 'description' });
                          e.currentTarget.closest('td')?.scrollIntoView({
                            inline: 'center',
                            block: 'nearest',
                            behavior: 'smooth',
                          });
                        }}
                        onBlur={() =>
                          setFocusedCell((prev) =>
                            prev?.rowId === row.id && prev.field === 'description' ? null : prev,
                          )
                        }
                      />
                    </td>
                    <td className={cn('border px-2 py-2 align-top transition-all', colDebitClass)}>
                      <DocumentRowInput
                        dir="ltr"
                        value={row.debit}
                        onChange={(v) => updateRow(row.id, 'debit', v)}
                        placeholder="0"
                        variant="amount"
                        disabled={loadingDoc}
                        active={focusedCell?.rowId === row.id && focusedCell.field === 'debit'}
                        onFocus={(e) => {
                          setFocusedCell({ rowId: row.id, field: 'debit' });
                          e.currentTarget.closest('td')?.scrollIntoView({
                            inline: 'center',
                            block: 'nearest',
                            behavior: 'smooth',
                          });
                        }}
                        onBlur={() =>
                          setFocusedCell((prev) =>
                            prev?.rowId === row.id && prev.field === 'debit' ? null : prev,
                          )
                        }
                      />
                    </td>
                    <td className={cn('border px-2 py-2 align-top transition-all', colCreditClass)}>
                      <DocumentRowInput
                        dir="ltr"
                        value={row.credit}
                        onChange={(v) => updateRow(row.id, 'credit', v)}
                        placeholder="0"
                        variant="amount"
                        disabled={loadingDoc}
                        active={focusedCell?.rowId === row.id && focusedCell.field === 'credit'}
                        onFocus={(e) => {
                          setFocusedCell({ rowId: row.id, field: 'credit' });
                          e.currentTarget.closest('td')?.scrollIntoView({
                            inline: 'center',
                            block: 'nearest',
                            behavior: 'smooth',
                          });
                        }}
                        onBlur={() =>
                          setFocusedCell((prev) =>
                            prev?.rowId === row.id && prev.field === 'credit' ? null : prev,
                          )
                        }
                      />
                    </td>
                    <td className="border px-2 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRow(row.id)}
                        disabled={rows.length <= 2}
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3">
            <Button type="button" variant="outline" className="gap-2" onClick={addRow}>
              <Plus className="h-4 w-4" />
              افزودن ردیف
            </Button>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
              <div className="text-xs text-slate-500">جمع بدهکار</div>
              <div className="mt-1 font-mono">{totalDebit.toLocaleString('en-US')}</div>
            </div>
            <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
              <div className="text-xs text-slate-500">جمع بستانکار</div>
              <div className="mt-1 font-mono">{totalCredit.toLocaleString('en-US')}</div>
            </div>
            <div
              className={`rounded-md border px-3 py-2 text-sm ${
                difference === 0
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-rose-50 text-rose-700'
              }`}
            >
              <div className="text-xs">اختلاف بدهکار/بستانکار</div>
              <div className="mt-1 font-semibold">
                {Math.abs(difference).toLocaleString('en-US')}
                {' '}
                {difference === 0 ? '(تراز)' : '(باید صفر شود)'}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/accounting-documents')}>
              انصراف
            </Button>
            <Button type="button" className="gap-2" disabled={difference !== 0 || saving || loadingDoc} onClick={saveDocument}>
              <Save className="h-4 w-4" />
              {saving ? 'در حال ذخیره...' : isEditMode ? 'ذخیره تغییرات سند' : 'ذخیره سند'}
            </Button>
          </div>
        </CardContent>
      </Card>

      </LoadingLock>
    </div>
  );
};

export default AccountingDocumentEntryPage;
